import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { trackCTAClick, trackCalculatorUse, trackDemoClick, trackFunnelStep, FUNNEL_STEPS } from '../lib/analytics';
import FAQSchema from '../components/FAQSchema';
import {
  Brain,
  Box,
  Sparkles,
  Layers,
  ArrowRight,
  Wrench,
  Eye,
  FileDown,
  CheckCircle,
  DollarSign,
  Clock,
  Scale,
  TreeDeciduous,
  Zap,
  Settings,
  Upload,
  Search,
  ChevronRight,
  ChevronDown,
  X,
  Mail,
} from 'lucide-react';

const PAGE_SECTIONS = [
  { id: 'hero', label: 'Home' },
  { id: 'demo', label: 'Demo' },
  { id: 'how-it-works', label: 'How It Works' },
  { id: 'results', label: 'Results' },
  { id: 'pricing', label: 'Pricing' },
  { id: 'faq', label: 'FAQ' },
];

const LandingPage = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('hero');
  const [filamentCost, setFilamentCost] = useState(20);
  // StrictMode double-mount guard: fire the funnel landing_view exactly
  // once per visitor session even if React.StrictMode dev-mounts the
  // component twice in development.
  const _landingFiredRef = useRef(false);

  useEffect(() => {
    if (_landingFiredRef.current) return;
    _landingFiredRef.current = true;
    // Mark the funnel landing-view exactly once per session so the
    // server-side drop-off math is accurate (page_view fires on every
    // SPA navigation; this fires only on the very first landing hit).
    trackFunnelStep(FUNNEL_STEPS.LANDING_VIEW, { source: document.referrer || 'direct' });

    const handleScroll = () => {
      const scrollY = window.scrollY + 200;
      for (let i = PAGE_SECTIONS.length - 1; i >= 0; i--) {
        const el = document.getElementById(PAGE_SECTIONS[i].id);
        if (el && el.offsetTop <= scrollY) {
          setActiveSection(PAGE_SECTIONS[i].id);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const pillars = [
    {
      title: 'AI Support Engine',
      sub: 'Intelligent Tree Supports',
      desc: 'Generate optimized tree supports that use up to 50% less material. Forest mode, hierarchical mode, batch processing, and lattice interface patterns for easy removal.',
      features: ['Forest & Hierarchical modes', 'Batch processing for production', 'Up to 50% material savings', 'Lattice interface for easy peel-off'],
      icon: Layers,
      color: 'green',
      gradient: 'from-green-500/20 to-emerald-500/10',
      border: 'border-green-500/30',
      subColor: 'text-green-700',
      iconBg: 'bg-green-500/20',
      iconColor: 'text-green-600',
      checkColor: 'text-green-600',
    },
    {
      title: 'Repair & Analysis',
      sub: 'One-Click STL Fixes',
      desc: 'Auto-detect mesh errors, non-manifold edges, and printability issues. One-click repair gets your models print-ready in seconds.',
      features: ['Auto geometry analysis', 'One-click mesh repair', 'Printability scoring', 'Seamless support generation after repair'],
      icon: Wrench,
      color: 'blue',
      gradient: 'from-blue-500/20 to-sky-500/10',
      border: 'border-blue-500/30',
      subColor: 'text-blue-700',
      iconBg: 'bg-blue-500/20',
      iconColor: 'text-blue-600',
      checkColor: 'text-blue-600',
    },
    {
      title: 'Vision Diagnosis & Quoting',
      sub: 'Fix Failures. Quote Clients.',
      desc: 'Upload print failure photos for AI diagnosis with exact slicer settings to change. Export professional PDF quotes with material costs and selling prices.',
      features: ['Exact slicer settings recommendations', 'Root cause failure analysis', 'Professional PDF quote export', 'Custom filament price profiles'],
      icon: Eye,
      color: 'orange',
      gradient: 'from-orange-500/20 to-amber-500/10',
      border: 'border-orange-500/30',
      subColor: 'text-orange-700',
      iconBg: 'bg-orange-500/20',
      iconColor: 'text-orange-600',
      checkColor: 'text-orange-600',
    },
  ];

  const comparisonRows = [
    { metric: 'Material Usage', smart: '3.2g', standard: '8.9g', savings: '64% less', highlight: true },
    { metric: 'Print Time', smart: '12 min', standard: '26 min', savings: '54% faster', highlight: false },
    { metric: 'Cost per Print', smart: '$0.06', standard: '$0.18', savings: '$0.12 saved', highlight: true },
    { metric: 'Support Removal', smart: 'Easy snap-off', standard: 'Pliers + sanding', savings: 'No tools needed', highlight: false },
    { metric: 'Surface Scarring', smart: 'Minimal', standard: 'Significant', savings: 'Cleaner finish', highlight: true },
    { metric: 'Complex Overhangs', smart: 'Optimized paths', standard: 'Block everywhere', savings: 'Smart placement', highlight: false },
  ];

  const steps = [
    { num: '01', title: 'Upload STL', desc: 'Drag & drop your 3D model', icon: Upload },
    { num: '02', title: 'Generate Supports', desc: 'AI creates optimized tree supports', icon: Zap },
    { num: '03', title: 'Download & Print', desc: 'Export STL, 3MF, or G-code', icon: FileDown },
  ];

  // Visible FAQ content — must stay in sync with the FAQPage JSON-LD
  // injected via <FAQSchema/> below. Google revokes rich-result
  // eligibility when schema text drifts from what's rendered on the
  // page, so any edit here should also be reflected in the rendered
  // accordion. Answers are deliberately short, factual, and reuse
  // landing-page copy.
  const faqs = [
    {
      question: 'How much filament can I save with PrintMind AI?',
      answer:
        'On typical models with overhangs, our AI-generated tree supports use up to 60% less filament than the block-style supports most slicers create by default. Exact savings depend on geometry, but every preview shows the before/after grams so you can verify the result before downloading.',
    },
    {
      question: 'What makes the AI-generated supports different from slicer supports?',
      answer:
        'Slicers place supports on a uniform grid wherever an overhang is detected. Our AI analyzes the mesh, places anchor points only where they are structurally needed, and routes thin organic branches that meet the model with a small lattice interface — so supports use less material, print faster, and snap off cleanly without scarring.',
    },
    {
      question: 'Does PrintMind AI repair broken STL files?',
      answer:
        'Yes. The platform auto-detects non-manifold edges, holes, and flipped normals before generating supports, then offers a one-click repair. If your STL was rejected by your slicer, fixing it here is usually enough to make it print-ready.',
    },
    {
      question: 'Is the output compatible with Cura, PrusaSlicer, OrcaSlicer, and Bambu Studio?',
      answer:
        'Yes. PrintMind AI exports a standard STL (or 3MF) with the supports merged into the model, so any FDM slicer treats it as a regular print. You typically disable your slicer’s built-in support generation, slice, and print as usual.',
    },
    {
      question: 'Is PrintMind AI suitable for beginners?',
      answer:
        'Yes — the default workflow is upload → generate → download, with no manual settings required. Power users can fine-tune support density, add or remove individual contact points, and switch tree styles after signing up, but none of that is needed for a first print.',
    },
  ];

  return (
    <div className="min-h-screen bg-bg-dark">
      {/* Floating Section Nav */}
      <nav
        className="fixed right-6 top-1/2 -translate-y-1/2 z-40 hidden lg:flex flex-col items-end gap-3"
        aria-label="Page sections"
        data-testid="section-nav"
      >
        {PAGE_SECTIONS.map((s) => (
          <button
            key={s.id}
            onClick={() => document.getElementById(s.id)?.scrollIntoView({ behavior: 'smooth' })}
            className="group flex items-center gap-2"
            aria-label={s.label}
          >
            <span
              className={`text-xs font-medium tracking-wide transition-all duration-200 ${
                activeSection === s.id
                  ? 'opacity-100 text-primary translate-x-0'
                  : 'opacity-0 group-hover:opacity-100 text-text-muted translate-x-2 group-hover:translate-x-0'
              }`}
            >
              {s.label}
            </span>
            <span
              className={`block rounded-full transition-all duration-200 ${
                activeSection === s.id
                  ? 'w-3 h-3 bg-primary shadow-[0_0_8px_rgba(var(--color-primary-rgb,99,102,241),0.5)]'
                  : 'w-2 h-2 bg-text-muted/30 group-hover:bg-text-muted/60'
              }`}
            />
          </button>
        ))}
      </nav>

      {/* Navbar */}
      <nav className="border-b border-text-muted/10 bg-bg-dark/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <div className="relative">
                <Brain className="w-8 h-8 text-primary" />
                <Box className="w-4 h-4 text-accent absolute -bottom-0.5 -right-0.5" />
              </div>
              <span className="text-2xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                PrintMind AI
              </span>
            </div>
            <div className="hidden sm:flex items-center space-x-6 text-sm font-medium">
              <a href="#features" className="text-text-primary/70 hover:text-text-primary transition">Features</a>
              <a href="#results" className="text-text-primary/70 hover:text-text-primary transition">ROI</a>
              <a href="#pricing" className="text-text-primary/70 hover:text-text-primary transition">Pricing</a>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate('/login')}
                className="text-text-primary/70 hover:text-text-primary transition text-sm font-medium"
              >
                Login
              </button>
              <button
                onClick={() => navigate('/register')}
                className="px-5 py-2 bg-primary hover:bg-primary/80 text-white rounded-lg transition-all font-medium text-sm shadow-md hover:shadow-lg hover:shadow-primary/25"
                data-testid="hero-get-started-btn"
              >
                Try Free
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="hero" className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-accent/10" />
        <div className="absolute top-20 left-1/4 w-72 h-72 bg-primary/8 rounded-full blur-[100px]" />
        <div className="absolute bottom-10 right-1/4 w-96 h-96 bg-accent/8 rounded-full blur-[120px]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
          <div className="text-center max-w-4xl mx-auto">
            {/* AI Platform positioning badge */}
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-gradient-to-r from-primary/20 to-accent/20 backdrop-blur-sm border border-primary/40 rounded-full text-primary text-xs sm:text-sm font-bold uppercase tracking-wider mb-4" data-testid="hero-ai-badge">
              <span className="text-base">⚡</span>
              AI-Powered 3D Printing Platform
            </div>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-primary/[0.06] backdrop-blur-sm border border-primary/20 rounded-full text-text-secondary text-sm font-medium mb-8">
              <Sparkles className="w-4 h-4 text-primary/70" />
              Smarter printing. Less filament. Fewer failures.
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-4 text-text-primary leading-tight">
              Fix 3D printing problems
              <span className="block bg-gradient-to-r from-amber-300 via-primary to-green-400 bg-clip-text text-transparent mt-2">
                before you print.
              </span>
            </h1>
            <p className="text-lg sm:text-xl text-text-secondary max-w-2xl mx-auto mb-2" data-testid="hero-subheading">
              Analyze STL files, detect print issues, and generate <span className="text-primary font-semibold">better supports</span> automatically.
            </p>
            <p className="text-sm text-green-400/90 font-medium mb-8" data-testid="hero-tagline">
              Save up to 60% filament on every print.
            </p>

            {/* Hero video — 1.5 MB MP4 served from our own origin so the
                browser doesn't reject the byte-range request the way it
                does for some CDN responses. Replace the file at
                /app/frontend/public/hero-demo.mp4 to swap the demo. */}
            <div className="max-w-2xl mx-auto mb-8" data-testid="hero-video-wrap">
              <div className="relative rounded-2xl overflow-hidden border border-text-muted/15 bg-bg-light/50 shadow-xl aspect-video">
                <video
                  className="w-full h-full object-cover"
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  data-testid="hero-video"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                >
                  {/* Two sources so every browser plays the format it supports.
                      Real Chrome, Edge, Safari, Firefox all play H.264 MP4
                      natively. WebM/VP9 covers open-source Chromium builds
                      (and is a clean fallback if MP4 fails for any reason). */}
                  <source src="/hero-demo.webm" type="video/webm" />
                  <source src="/hero-demo.mp4" type="video/mp4" />
                </video>
                {/* Subtle "Live demo" badge over the video */}
                <div className="absolute top-3 left-3 px-2.5 py-1 bg-bg-dark/70 backdrop-blur-sm border border-primary/30 rounded-full text-[10px] uppercase tracking-widest font-bold text-primary flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                  Live demo
                </div>
              </div>
            </div>

            {/* Urgency / scarcity line directly above CTA — reinforces "act now" */}
            <p className="text-xs sm:text-sm text-amber-300 font-semibold mb-3 flex items-center justify-center gap-2" data-testid="hero-urgency">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-300 animate-pulse" />
              Free during beta — get yours before pricing changes
            </p>

            {/* Single primary CTA — wrapped in pulsing ring for visibility */}
            <div className="flex justify-center">
              <div className="relative inline-block group">
                {/* Soft pulsing halo behind the button */}
                <span className="absolute inset-0 rounded-xl bg-primary/40 blur-xl opacity-60 group-hover:opacity-90 animate-pulse pointer-events-none" aria-hidden="true" />
                <button
                  onClick={() => { trackCTAClick('Generate Supports Now', 'hero'); navigate('/preview'); }}
                  className="relative px-10 py-4 bg-primary hover:bg-primary/80 text-white rounded-xl transition-all font-bold text-lg flex items-center justify-center gap-2 shadow-lg shadow-primary/30 hover:shadow-xl hover:shadow-primary/50 hover:scale-[1.03] ring-2 ring-primary/60 ring-offset-2 ring-offset-bg-dark"
                  data-testid="hero-start-free-btn"
                >
                  <Zap className="w-5 h-5" />
                  Generate Supports Now
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* 3-bullet trust strip — visually separates benefits from CTA */}
            <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1.5 mt-4 text-xs sm:text-sm text-text-secondary" data-testid="hero-trust-strip">
              <span className="flex items-center gap-1.5">
                <CheckCircle className="w-3.5 h-3.5 text-green-400" />
                No signup
              </span>
              <span className="flex items-center gap-1.5">
                <CheckCircle className="w-3.5 h-3.5 text-green-400" />
                10-second generation
              </span>
              <span className="flex items-center gap-1.5">
                <CheckCircle className="w-3.5 h-3.5 text-green-400" />
                Save up to 62% filament
              </span>
            </div>

            {/* Microcopy under CTA */}
            <p className="text-xs text-text-muted/70 mt-3" data-testid="hero-credibility">
              Used by makers to reduce print failures and save filament.
            </p>
          </div>
        </div>
      </section>

      {/* ============ MORE THAN JUST SUPPORTS — placed early so users
              immediately see this is a full AI platform, not a supports-only
              tool. ============ */}
      <section id="more-than-supports" className="py-20 bg-bg-dark" data-testid="more-than-supports-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-accent/10 border border-accent/20 rounded-full text-xs font-bold text-accent uppercase tracking-wider mb-4">
              <Sparkles className="w-3.5 h-3.5" />
              AI Platform
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-text-primary mb-3 leading-tight">
              More than just supports
            </h2>
            <div className="flex items-center justify-center gap-2 mb-3">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 bg-gradient-to-r from-primary/20 to-accent/20 border border-primary/30 rounded-full text-[10px] font-bold text-primary uppercase tracking-wider" data-testid="powered-by-ai-badge">
                <span>⚡</span>
                Powered by AI
              </span>
            </div>
            <p className="text-base sm:text-lg text-text-secondary max-w-2xl mx-auto" data-testid="more-than-supports-subtitle">
              One AI engine that prepares your entire model for perfect printing.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="more-than-supports-grid">
            {[
              {
                icon: Wrench,
                title: 'AI STL Repair',
                desc: 'Auto-repair holes & flipped normals.',
                tone: 'accent',
              },
              {
                icon: Search,
                title: 'AI Failure Detection',
                desc: 'Spot risks before you print.',
                tone: 'primary',
              },
              {
                icon: Settings,
                title: 'AI Optimization Engine',
                desc: 'Smart orientation & rotation.',
                tone: 'green',
              },
              {
                icon: FileDown,
                title: 'AI-Generated Print Files',
                desc: 'Supports baked into your STL.',
                tone: 'primary',
              },
              {
                icon: Sparkles,
                title: 'Fine-tune your supports',
                desc: 'Add, remove, or adjust points.',
                tone: 'accent',
              },
            ].map((it, i) => {
              const toneMap = {
                primary: 'bg-primary/10 border-primary/20 text-primary',
                accent: 'bg-accent/10 border-accent/20 text-accent',
                green: 'bg-green-500/10 border-green-500/20 text-green-400',
              };
              return (
                <div
                  key={i}
                  className="group relative bg-bg-light/60 rounded-2xl border border-text-muted/10 p-5 hover:border-primary/30 transition-all hover:translate-y-[-2px]"
                  data-testid={`more-than-supports-card-${i}`}
                >
                  <div className={`inline-flex w-11 h-11 rounded-xl items-center justify-center border ${toneMap[it.tone]} mb-3.5`}>
                    <it.icon className="w-5 h-5" />
                  </div>
                  <h3 className="text-sm font-bold text-text-primary mb-1.5 leading-snug">{it.title}</h3>
                  <p className="text-xs text-text-secondary leading-relaxed">{it.desc}</p>
                </div>
              );
            })}
          </div>

          <div className="text-center mt-10">
            <button
              onClick={() => { trackCTAClick('Explore the full platform', 'more-than-supports'); navigate('/preview'); }}
              className="inline-flex items-center justify-center gap-2 px-7 py-3 bg-bg-light border-2 border-text-muted/20 hover:border-primary/40 text-text-primary rounded-xl font-semibold text-sm transition-all"
              data-testid="more-than-supports-cta"
            >
              Explore the full platform
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* ============ MERGED-STL VALUE PROP (BEFORE / AFTER) ============ */}
      <section id="merged-stl" className="py-20 bg-gradient-to-b from-bg-dark via-bg-light/30 to-bg-dark" data-testid="merged-stl-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-primary/10 border border-primary/20 rounded-full text-xs font-bold text-primary uppercase tracking-wider mb-4">
              <Sparkles className="w-3.5 h-3.5" />
              The PrintMind difference
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-text-primary mb-4 leading-tight">
              Build supports once. <span className="bg-gradient-to-r from-primary to-green-400 bg-clip-text text-transparent">Reuse forever.</span>
            </h2>
          </div>

          {/* Before / After visual */}
          <div className="grid md:grid-cols-2 gap-6 mb-10" data-testid="merged-stl-comparison">
            {/* BEFORE */}
            <div className="bg-bg-light/60 rounded-2xl border border-orange-500/20 p-6">
              <div className="flex items-center gap-2 mb-5">
                <span className="px-2.5 py-0.5 bg-orange-500/15 border border-orange-500/30 rounded-md text-[10px] font-bold text-orange-300 uppercase tracking-widest">Before</span>
                <span className="text-sm text-text-muted">Slicer-only workflow</span>
              </div>
              <div className="space-y-3">
                {[
                  { icon: Box, label: 'Your STL model', tone: 'neutral' },
                  { icon: Settings, label: 'Slicer regenerates supports each print', tone: 'warn' },
                  { icon: Clock, label: 'Repeat every single time', tone: 'warn' },
                ].map((row, i) => (
                  <div key={i} className={`flex items-center gap-3 px-3.5 py-2.5 rounded-lg bg-bg-dark/60 border ${row.tone === 'warn' ? 'border-orange-500/15' : 'border-text-muted/10'}`}>
                    <row.icon className={`w-4 h-4 flex-shrink-0 ${row.tone === 'warn' ? 'text-orange-300/80' : 'text-text-muted'}`} />
                    <span className="text-sm text-text-secondary">{row.label}</span>
                  </div>
                ))}
              </div>
              <div className="mt-5 pt-4 border-t border-text-muted/10 flex items-center gap-2 text-xs text-orange-300/80">
                <Clock className="w-3.5 h-3.5" />
                Slow, error-prone, settings drift between prints
              </div>
            </div>

            {/* AFTER */}
            <div className="relative bg-gradient-to-br from-primary/10 via-bg-light to-green-500/10 rounded-2xl border-2 border-primary/30 p-6 shadow-xl shadow-primary/10">
              <div className="absolute -top-3 right-5 px-3 py-1 bg-primary text-white text-[10px] font-bold rounded-full uppercase tracking-widest shadow-lg shadow-primary/40">PrintMind</div>
              <div className="flex items-center gap-2 mb-5">
                <span className="px-2.5 py-0.5 bg-green-500/15 border border-green-500/30 rounded-md text-[10px] font-bold text-green-400 uppercase tracking-widest">After</span>
                <span className="text-sm text-text-secondary font-medium">One STL — supports already built in</span>
              </div>
              <div className="space-y-3">
                {[
                  { icon: Layers, label: 'Supports merged directly into your STL', emphasis: true },
                  { icon: Zap, label: 'No need to regenerate supports each print' },
                  { icon: TreeDeciduous, label: 'Faster workflow, fewer slicer errors' },
                ].map((row, i) => (
                  <div key={i} className={`flex items-center gap-3 px-3.5 py-2.5 rounded-lg ${row.emphasis ? 'bg-primary/15 border border-primary/30' : 'bg-bg-dark/60 border border-green-500/15'}`}>
                    <row.icon className={`w-4 h-4 flex-shrink-0 ${row.emphasis ? 'text-primary' : 'text-green-400'}`} />
                    <span className={`text-sm ${row.emphasis ? 'text-text-primary font-semibold' : 'text-text-secondary'}`}>{row.label}</span>
                  </div>
                ))}
              </div>
              <div className="mt-5 pt-4 border-t border-primary/15 flex items-center gap-2 text-xs text-green-400 font-semibold">
                <CheckCircle className="w-3.5 h-3.5" />
                Print, reprint, share — supports stay baked in
              </div>
            </div>
          </div>

          <div className="text-center">
            <button
              onClick={() => { trackCTAClick('Get my ready-to-print STL', 'merged-stl-section'); navigate('/preview'); }}
              className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-primary hover:bg-primary/80 text-white rounded-xl font-bold text-base shadow-lg shadow-primary/25 hover:scale-[1.02] transition-all"
              data-testid="merged-stl-cta"
            >
              <FileDown className="w-5 h-5" />
              Get my ready-to-print STL
              <ArrowRight className="w-4 h-4" />
            </button>
            <p className="text-xs text-text-muted mt-3">Free preview — no credit card required</p>
          </div>
        </div>
      </section>

      {/* ============ AI FEATURE STRIP ============ */}
      <section className="py-10 bg-bg-light/20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div className="flex flex-col items-center gap-2">
              <div className="w-10 h-10 rounded-lg bg-primary/15 flex items-center justify-center">
                <Brain className="w-5 h-5 text-primary" />
              </div>
              <p className="text-xs sm:text-sm text-text-secondary font-medium">Detect print failures with AI</p>
            </div>
            <div className="flex flex-col items-center gap-2">
              <div className="w-10 h-10 rounded-lg bg-accent/15 flex items-center justify-center">
                <Wrench className="w-5 h-5 text-accent" />
              </div>
              <p className="text-xs sm:text-sm text-text-secondary font-medium">Fix broken STL files instantly</p>
            </div>
            <div className="flex flex-col items-center gap-2">
              <div className="w-10 h-10 rounded-lg bg-green-500/15 flex items-center justify-center">
                <Zap className="w-5 h-5 text-green-400" />
              </div>
              <p className="text-xs sm:text-sm text-text-secondary font-medium">Optimize supports & save filament</p>
            </div>
          </div>
        </div>
      </section>

      {/* ============ SAVINGS CALCULATOR ============ */}
      <section className="py-12 bg-bg-dark">
        <div className="max-w-md mx-auto px-4 sm:px-6">
          <div className="bg-bg-light/60 backdrop-blur-sm rounded-2xl border border-primary/15 p-6 shadow-lg animate-[subtlePulse_3s_ease-in-out_1s_1]" data-testid="savings-calculator">
            <h3 className="text-base font-bold text-text-primary text-center mb-1">How Much Are You Wasting?</h3>
            <p className="text-xs text-text-muted text-center mb-5">Enter your filament cost to see your savings</p>

            <div className="flex items-center gap-3 mb-4">
              <label className="text-sm text-text-secondary whitespace-nowrap">Filament $/kg:</label>
              <input
                type="range"
                min="10"
                max="60"
                value={filamentCost}
                onChange={(e) => setFilamentCost(Number(e.target.value))}
                className="flex-1 accent-primary h-1.5"
                data-testid="filament-cost-slider"
              />
              <span className="text-sm text-primary font-bold w-10 text-right">${filamentCost}</span>
            </div>

            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="bg-bg-dark/60 rounded-lg p-3">
                <p className="text-lg font-black text-green-400">${((75 / 1000) * filamentCost).toFixed(2)}</p>
                <p className="text-[10px] text-text-muted">saved per print</p>
              </div>
              <div className="bg-bg-dark/60 rounded-lg p-3">
                <p className="text-lg font-black text-green-400">${((75 / 1000) * filamentCost * 30).toFixed(0)}</p>
                <p className="text-[10px] text-text-muted">saved per month</p>
              </div>
              <div className="bg-bg-dark/60 rounded-lg p-3">
                <p className="text-lg font-black text-green-400">{((75 * 30) / 1000).toFixed(1)}kg</p>
                <p className="text-[10px] text-text-muted">filament saved/mo</p>
              </div>
            </div>

            <button
              onClick={() => { trackCalculatorUse(filamentCost); trackCTAClick('Start Saving Now', 'calculator'); navigate('/preview'); }}
              className="w-full mt-4 py-2.5 bg-primary hover:bg-primary/80 text-white rounded-xl font-bold text-sm transition-all shadow-md shadow-primary/20 hover:scale-[1.02] flex items-center justify-center gap-2"
              data-testid="calculator-cta"
            >
              <Zap className="w-4 h-4" />
              Start Saving Now
            </button>
          </div>
        </div>
      </section>

      {/* ============ QUICK DEMO ============ */}
      <section id="demo" className="py-24 bg-bg-light/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-text-primary mb-3">Smarter supports. Better prints.</h2>
            <p className="text-text-secondary text-lg max-w-xl mx-auto">
              No upload required. We'll load a sample model and generate supports instantly.
            </p>
          </div>

          <div className="bg-gradient-to-br from-primary/10 via-bg-light to-green-500/10 rounded-2xl border border-primary/20 p-8" data-testid="demo-block">
            <div className="max-w-lg mx-auto text-center">
              {/* Before / After */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-bg-dark/60 rounded-xl p-5 border border-text-muted/15">
                  <p className="text-[10px] uppercase tracking-widest text-orange-300/70 font-bold mb-3">Before</p>
                  <p className="text-3xl font-black text-orange-300/60 mb-1">120g</p>
                  <p className="text-xs text-text-muted">Block supports</p>
                  <p className="text-[10px] text-text-muted/50 mt-1">Messy &middot; Hard to remove</p>
                </div>
                <div className="bg-bg-dark/60 rounded-xl p-5 border border-green-400/20">
                  <p className="text-[10px] uppercase tracking-widest text-green-400 font-bold mb-3">After</p>
                  <p className="text-3xl font-black text-green-400 mb-1">45g</p>
                  <p className="text-xs text-text-muted">Smart tree supports</p>
                  <p className="text-[10px] text-green-400/50 mt-1">Clean &middot; Easy snap-off</p>
                </div>
              </div>

              {/* Impact line — money framing */}
              <p className="text-sm text-green-400 font-semibold mb-5">
                That's 75g saved per print — <span className="text-green-300">~$2/print</span> &rarr; <span className="text-green-300 font-bold">$60/month</span>
              </p>

              <button
                onClick={() => { trackDemoClick(); trackCTAClick('Try Demo Now', 'demo'); navigate('/preview'); }}
                className="px-8 py-3.5 bg-primary hover:bg-primary/80 text-white rounded-xl transition-all font-bold text-base flex items-center justify-center gap-2 mx-auto shadow-lg shadow-primary/25 hover:shadow-xl hover:scale-[1.02]"
                data-testid="demo-try-btn"
              >
                <Zap className="w-5 h-5" />
                Try Demo Now (No Upload Required)
              </button>
              <p className="text-xs text-text-muted mt-3">Free forever &middot; No credit card &middot; Instant results</p>
            </div>
          </div>
        </div>
      </section>

      {/* ============ HOW IT WORKS ============ */}
      <section id="how-it-works" className="py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-text-primary mb-3">How It Works</h2>
            <p className="text-text-secondary text-lg">Three steps. Under 30 seconds.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {steps.map((step, i) => {
              const Icon = step.icon;
              return (
                <div key={step.num} className="relative" data-testid={`step-${step.num}`}>
                  <div className="bg-bg-light p-6 rounded-2xl border border-text-muted/15 text-center h-full">
                    <span className="text-4xl font-black text-primary/15 block mb-2">{step.num}</span>
                    <div className="w-11 h-11 mx-auto mb-3 rounded-lg bg-primary/15 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-primary" />
                    </div>
                    <h3 className="text-base font-bold text-text-primary mb-1">{step.title}</h3>
                    <p className="text-sm text-text-secondary">{step.desc}</p>
                  </div>
                  {i < steps.length - 1 && (
                    <div className="hidden md:flex absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                      <ChevronRight className="w-6 h-6 text-text-muted/30" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Repeated CTA - how it works */}
          <div className="text-center mt-10">
            <button
              onClick={() => { trackCTAClick('Generate Supports Now', 'how-it-works'); navigate('/register'); }}
              className="px-8 py-3.5 bg-primary hover:bg-primary/80 text-white rounded-xl transition-all font-bold text-base inline-flex items-center gap-2 shadow-lg shadow-primary/25 hover:shadow-xl hover:scale-[1.02]"
            >
              <Zap className="w-5 h-5" />
              Generate Supports Now
            </button>
            <p className="text-xs text-text-muted mt-3">Free forever &middot; No credit card &middot; Instant results</p>
          </div>
        </div>
      </section>

      {/* ============ RESULTS / ROI ============ */}
      <section id="results" className="py-24 bg-bg-light/30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-green-500/10 border border-green-500/20 rounded-full text-green-400 text-sm font-medium mb-5">
              <Scale className="w-4 h-4" />
              Proven Results
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-text-primary mb-3">Real Results. Real Savings.</h2>
            <p className="text-text-secondary text-lg">See how much you save on every single print</p>
          </div>

          {/* Big stats row */}
          <div className="grid grid-cols-3 gap-4 mb-10">
            <div className="bg-bg-light rounded-2xl p-6 border border-green-500/15 text-center">
              <p className="text-4xl sm:text-5xl font-black text-green-400 mb-1">62%</p>
              <p className="text-sm text-text-secondary font-medium">Less Material</p>
              <p className="text-xs text-text-muted mt-1">vs block supports</p>
            </div>
            <div className="bg-bg-light rounded-2xl p-6 border border-green-500/15 text-center">
              <p className="text-4xl sm:text-5xl font-black text-green-400 mb-1">54%</p>
              <p className="text-sm text-text-secondary font-medium">Faster Prints</p>
              <p className="text-xs text-text-muted mt-1">less support to print</p>
            </div>
            <div className="bg-bg-light rounded-2xl p-6 border border-green-500/15 text-center">
              <p className="text-4xl sm:text-5xl font-black text-green-400 mb-1">Easy</p>
              <p className="text-sm text-text-secondary font-medium">Snap-off Removal</p>
              <p className="text-xs text-text-muted mt-1">no pliers needed</p>
            </div>
          </div>

          {/* Comparison table */}
          <div className="bg-bg-light rounded-2xl border border-text-muted/20 overflow-hidden">
            <div className="grid grid-cols-4 bg-bg-dark/60 text-sm font-semibold">
              <div className="p-4 text-text-muted">Metric</div>
              <div className="p-4 text-center">
                <span className="inline-flex items-center gap-1.5 text-green-400">
                  <TreeDeciduous className="w-4 h-4" /> PrintMind
                </span>
              </div>
              <div className="p-4 text-center text-text-muted">Standard</div>
              <div className="p-4 text-center text-text-muted">Savings</div>
            </div>
            {comparisonRows.map((row) => (
              <div
                key={row.metric}
                className={`grid grid-cols-4 text-sm border-t border-text-muted/10 ${
                  row.highlight ? 'bg-green-500/[0.03]' : ''
                }`}
              >
                <div className="p-4 text-text-primary font-medium">{row.metric}</div>
                <div className="p-4 text-center text-green-400 font-semibold">{row.smart}</div>
                <div className="p-4 text-center text-text-muted">{row.standard}</div>
                <div className="p-4 text-center">
                  <span className="inline-block px-2.5 py-1 bg-green-500/15 text-green-400 rounded-full text-xs font-medium">
                    {row.savings}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Repeated CTA - results */}
          <div className="text-center mt-10">
            <button
              onClick={() => { trackCTAClick('Generate Supports Now', 'results'); navigate('/preview'); }}
              className="px-8 py-3.5 bg-primary hover:bg-primary/80 text-white rounded-xl transition-all font-bold text-base inline-flex items-center gap-2 shadow-lg shadow-primary/25 hover:shadow-xl hover:scale-[1.02]"
            >
              <Zap className="w-5 h-5" />
              Generate Supports Now
            </button>
            <p className="text-xs text-text-muted mt-3">Free forever &middot; No credit card &middot; Instant results</p>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold text-text-primary mb-3">Simple, Transparent Pricing</h2>
            <p className="text-text-secondary text-lg">Start free. Upgrade when you need more power.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {/* Free Plan */}
            <div className="bg-bg-light p-7 rounded-2xl border border-text-muted/20" data-testid="pricing-free">
              <h3 className="text-xl font-bold text-text-primary mb-1">Free</h3>
              <p className="text-text-muted text-sm mb-4">For hobbyists getting started</p>
              <div className="mb-6">
                <span className="text-4xl font-bold text-text-primary">$0</span>
                <span className="text-text-muted text-sm">/month</span>
              </div>
              <ul className="space-y-3 mb-8 text-sm">
                {[
                  '5 STL analyses & repairs',
                  '3 support generations',
                  '3 image diagnoses',
                  'Forest & Hierarchical modes',
                  'ROI savings dashboard',
                ].map((f) => (
                  <li key={f} className="flex items-start gap-2 text-text-secondary">
                    <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => navigate('/register')}
                className="w-full py-3 bg-bg-dark border border-text-muted/20 text-text-primary rounded-xl hover:border-primary/40 transition font-medium"
              >
                Get Started
              </button>
            </div>

            {/* Pro Plan */}
            <div className="bg-gradient-to-br from-primary/10 to-accent/10 p-7 rounded-2xl border-2 border-primary relative shadow-lg shadow-primary/10" data-testid="pricing-pro">
              <div className="absolute -top-3.5 left-1/2 transform -translate-x-1/2 flex items-center gap-2">
                <span className="px-4 py-1 bg-primary rounded-full text-xs font-bold text-white tracking-wide">BEST VALUE</span>
              </div>
              <h3 className="text-xl font-bold text-text-primary mb-1">Pro</h3>
              <p className="text-text-muted text-sm mb-4">For serious makers & small shops</p>
              <div className="mb-6">
                <span className="text-4xl font-bold text-text-primary">$14</span>
                <span className="text-text-muted text-sm">/month</span>
              </div>
              <ul className="space-y-3 mb-8 text-sm">
                {[
                  'Everything in Free',
                  '50 STL analyses & 100 repairs',
                  '25 support generations',
                  '30 image diagnoses',
                  'Professional PDF quote export',
                  'Custom filament price profiles',
                  'AI slicer setting recommendations',
                  'Priority processing',
                ].map((f) => (
                  <li key={f} className="flex items-start gap-2 text-text-secondary">
                    <CheckCircle className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => navigate('/register')}
                className="w-full py-3 bg-primary hover:bg-primary/90 text-white rounded-xl transition font-semibold"
              >
                Start Pro Trial
              </button>
            </div>

            {/* Studio Plan */}
            <div className="bg-bg-light p-7 rounded-2xl border border-text-muted/20" data-testid="pricing-studio">
              <h3 className="text-xl font-bold text-text-primary mb-1">Studio</h3>
              <p className="text-text-muted text-sm mb-4">For print farms & agencies</p>
              <div className="mb-6">
                <span className="text-4xl font-bold text-text-primary">$29</span>
                <span className="text-text-muted text-sm">/month</span>
              </div>
              <ul className="space-y-3 mb-8 text-sm">
                {[
                  'Everything in Pro',
                  '200 STL analyses & 500 repairs',
                  '100 support generations',
                  '100 image diagnoses',
                  'Batch processing (up to 10 files)',
                  'Team access (3 seats)',
                  'API access',
                  'Dedicated support',
                ].map((f) => (
                  <li key={f} className="flex items-start gap-2 text-text-secondary">
                    <CheckCircle className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => navigate('/register')}
                className="w-full py-3 bg-bg-dark border border-accent/30 text-text-primary rounded-xl hover:border-accent/60 transition font-medium"
              >
                Get Studio
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FAQ — visible Q&A backing the FAQPage schema ============ */}
      <FAQSchema faqs={faqs} />
      <section id="faq" className="py-20 bg-bg-dark" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-primary/10 border border-primary/20 rounded-full text-xs font-bold text-primary uppercase tracking-wider mb-4">
              <Sparkles className="w-3.5 h-3.5" />
              Frequently asked
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-text-primary mb-2 leading-tight">
              Everything you need to know
            </h2>
            <p className="text-text-secondary text-base">
              Honest answers — no hype.
            </p>
          </div>

          <div className="space-y-3" data-testid="faq-list">
            {faqs.map((f, i) => (
              <details
                key={i}
                className="group bg-bg-light/60 rounded-xl border border-text-muted/10 hover:border-primary/25 transition-colors open:border-primary/30"
                data-testid={`faq-item-${i}`}
              >
                <summary className="flex items-center justify-between gap-4 cursor-pointer list-none px-5 py-4">
                  {/* itemProp/itemScope mirrors the JSON-LD answer scope so
                      Google can pair the visible content with the schema. */}
                  <h3 className="text-base font-semibold text-text-primary leading-snug">
                    {f.question}
                  </h3>
                  <ChevronDown className="w-5 h-5 text-text-muted flex-shrink-0 transition-transform group-open:rotate-180" />
                </summary>
                <div className="px-5 pb-5 -mt-1">
                  <p className="text-sm text-text-secondary leading-relaxed">
                    {f.answer}
                  </p>
                </div>
              </details>
            ))}
          </div>

          <p className="text-center text-xs text-text-muted mt-6">
            Still have questions?{' '}
            <a href="mailto:support@printmind.ai" className="text-primary hover:underline">
              support@printmind.ai
            </a>
          </p>
        </div>
      </section>

      {/* ============ AI COPILOT TOOLS ============ */}
      <section id="features" className="py-14 bg-bg-light/15">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-sm text-text-secondary mb-6 max-w-xl mx-auto leading-relaxed">
            PrintMind AI doesn't just optimize supports — it understands your prints and helps you fix problems before they fail.
          </p>
          <div className="text-center mb-8">
            <h2 className="text-xl sm:text-2xl font-bold text-text-primary/80 mb-1">Fix Your Prints Before They Fail</h2>
            <p className="text-text-muted text-sm">AI-powered diagnostics, repair, and optimization — all in one platform</p>
          </div>

          <div className="grid lg:grid-cols-3 gap-4">
            {pillars.map((p) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.title}
                  className="bg-bg-light/50 p-5 rounded-xl border border-white/[0.08] hover:-translate-y-0.5 hover:border-white/[0.15] hover:shadow-lg hover:shadow-white/[0.03] transition-all duration-200"
                  data-testid={`pillar-${p.title.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className="flex items-center gap-3 mb-2.5">
                    <div className={`w-9 h-9 ${p.iconBg} rounded-lg flex items-center justify-center`}>
                      <Icon className={`w-4 h-4 ${p.iconColor}`} />
                    </div>
                    <h3 className="text-sm font-semibold text-text-primary/90">{p.title}</h3>
                  </div>
                  <p className="text-xs text-text-secondary/70 leading-relaxed">{p.desc.substring(0, 110)}...</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-[#0F172A]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-base text-primary/60 font-medium mb-4">Stop guessing. Let AI guide your prints.</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">Stop Wasting Filament Today</h2>
          <p className="text-gray-300 text-lg mb-4">
            Generate your first optimized support for free — see the savings instantly.
          </p>
          <p className="text-sm text-primary font-semibold mb-8">
            Join early users before pricing increases
          </p>
          <button
            onClick={() => { trackCTAClick('Start Saving Now', 'final-cta'); navigate('/preview'); }}
            className="px-10 py-4 bg-primary hover:bg-primary/80 text-white rounded-xl transition-all font-bold text-lg flex items-center gap-2 mx-auto shadow-lg shadow-primary/30 hover:shadow-xl hover:scale-[1.02]"
          >
            <Upload className="w-5 h-5" />
            Upload Your STL — See Results
            <ArrowRight className="w-5 h-5" />
          </button>
          <p className="text-xs text-gray-500 mt-3">Free preview &middot; No signup required &middot; Instant results</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 bg-[#0F172A]" data-testid="landing-footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-10 mb-12">
            {/* Brand */}
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center space-x-3 mb-4">
                <div className="relative">
                  <Brain className="w-7 h-7 text-indigo-400" />
                  <Box className="w-3.5 h-3.5 text-cyan-400 absolute -bottom-0.5 -right-0.5" />
                </div>
                <span className="text-lg font-bold bg-gradient-to-r from-indigo-400 to-cyan-400 bg-clip-text text-transparent">
                  PrintMind AI
                </span>
              </div>
              <p className="text-sm text-gray-400 leading-relaxed max-w-xs">
                The complete AI toolkit for 3D printing professionals. Save material, fix failures, print smarter.
              </p>
            </div>

            {/* Product links */}
            <div>
              <h4 className="text-sm font-semibold text-white mb-4 uppercase tracking-wider">Product</h4>
              <ul className="space-y-2.5">
                {[
                  { label: 'AI Support Engine', href: '#features' },
                  { label: 'Repair & Analysis', href: '#features' },
                  { label: 'Vision Diagnosis', href: '#features' },
                  { label: 'ROI Dashboard', href: '#results' },
                ].map((l) => (
                  <li key={l.label}>
                    <a href={l.href} className="text-sm text-gray-400 hover:text-white transition">
                      {l.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Resource links */}
            <div>
              <h4 className="text-sm font-semibold text-white mb-4 uppercase tracking-wider">Resources</h4>
              <ul className="space-y-2.5">
                {[
                  { label: 'How It Works', href: '#how-it-works' },
                  { label: 'Pricing', href: '#pricing' },
                  { label: 'Login', action: () => navigate('/login') },
                  { label: 'Sign Up Free', action: () => navigate('/register') },
                ].map((l) => (
                  <li key={l.label}>
                    {l.action ? (
                      <button onClick={l.action} className="text-sm text-gray-400 hover:text-white transition">
                        {l.label}
                      </button>
                    ) : (
                      <a href={l.href} className="text-sm text-gray-400 hover:text-white transition">
                        {l.label}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="text-sm font-semibold text-white mb-4 uppercase tracking-wider">Legal</h4>
              <ul className="space-y-2.5">
                <li>
                  <button onClick={() => navigate('/privacy')} className="text-sm text-gray-400 hover:text-white transition" data-testid="footer-privacy-link">
                    Privacy Policy
                  </button>
                </li>
                <li>
                  <button onClick={() => navigate('/terms')} className="text-sm text-gray-400 hover:text-white transition" data-testid="footer-terms-link">
                    Terms of Service
                  </button>
                </li>
              </ul>
            </div>

            {/* Compatibility */}
            <div>
              <h4 className="text-sm font-semibold text-white mb-4 uppercase tracking-wider">Works With</h4>
              <ul className="space-y-2.5">
                {['Cura', 'PrusaSlicer', 'OrcaSlicer', 'Bambu Studio', 'Any FDM Slicer'].map((s) => (
                  <li key={s} className="text-sm text-gray-400 flex items-center gap-1.5">
                    <CheckCircle className="w-3.5 h-3.5 text-green-400" />
                    {s}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="border-t border-white/10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-gray-500 text-xs">
              &copy; 2026 Harbor Momentum Group LLC. All rights reserved.
            </p>
            <div className="flex items-center gap-4 text-xs text-gray-400">
              <button onClick={() => navigate('/privacy')} className="hover:text-white transition">Privacy</button>
              <button onClick={() => navigate('/terms')} className="hover:text-white transition">Terms</button>
              <a href="mailto:support@printmind.ai" className="hover:text-white transition flex items-center gap-1">
                <Mail className="w-3.5 h-3.5" />
                support@printmind.ai
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Sticky CTA — visible on scroll */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-bg-dark/95 backdrop-blur-md border-t border-text-muted/15 px-4 py-2.5 translate-y-full animate-[slideUp_0.3s_ease-out_2s_forwards]" data-testid="sticky-cta">
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
          <p className="hidden sm:block text-sm text-text-muted">
            <span className="text-green-400 font-semibold">Save 62%</span> filament on every print
          </p>
          <button
            onClick={() => { trackCTAClick('Start Saving Now', 'sticky'); navigate('/preview'); }}
            className="w-full sm:w-auto px-6 py-2.5 bg-primary hover:bg-primary/80 text-white rounded-xl font-bold text-sm flex items-center justify-center gap-2 shadow-lg shadow-primary/30"
          >
            <Upload className="w-4 h-4" />
            Upload Your STL — Free Preview
          </button>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
